import { useForm, UseFormProps } from 'react-hook-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { UpdateUserAddressDto, UpdateUserDto, UserDto } from '@sweatpals/shared';
import { compact } from 'lodash-es';
import { EditCommunityFormData } from '../edit-community';
import { EditCommunityFormValidator } from '../../../models/dto';
import { GetAddressInfoFromAddressCallback } from './use-create-community-flow-state.hook';

const resolver = classValidatorResolver(EditCommunityFormValidator);

export const useEditCommunityFlowForm = <Context = any>(props?: UseFormProps<EditCommunityFormValidator, Context>) => {
	const form = useForm<EditCommunityFormData>({
		...props,
		mode: 'onBlur',
		resolver,
	});

	const formData = form.watch();

	return {
		form,
		formData,
		errors: form.formState.errors,
	};
};

export const prepareEditCommunityDataBeforeSubmit = async (
	data: EditCommunityFormData,
	communityProfile: UserDto,
	getAddressInfoFromAddress: GetAddressInfoFromAddressCallback,
): Promise<UpdateUserDto> => {
	const { ...rest } = data;

	if (rest.addresses?.length) {
		const placeOptions = rest.addresses;

		if (!getAddressInfoFromAddress) {
			throw new Error('Google Maps API method is required to get the location details');
		}

		rest.updatedAddresses = await Promise.all(
			placeOptions.map(async (place) => {
				const addressInfo = await getAddressInfoFromAddress(place.description);

				return {
					addressName: place.description,
					addressLat: addressInfo.lat.toString(),
					addressLng: addressInfo.lng.toString(),
				} as UpdateUserAddressDto;
			})
		);
	}

	if (communityProfile.fullName === rest.fullName) {
		rest.fullName = undefined;
	}

	if (communityProfile.userName === rest.userName) {
		rest.userName = undefined;
	}

	const oldImages = communityProfile.files?.filter(file => file.mimeType.startsWith('image/')) ?? [];
	const oldImageIds = oldImages.map(file => file.id);
	const imageIds = compact((rest.imagesToUpload ?? []).map(image => image.key));
	const fileIdsToDelete = oldImageIds.filter(id => !imageIds.includes(id));
	const imagesToUpload = (rest.imagesToUpload ?? []).filter(image => !image.key);
	rest.imagesToUpload = undefined;

	if (fileIdsToDelete.length) {
		rest.fileIdsToDelete = fileIdsToDelete;
	}

	if (imagesToUpload.length) {
		rest.imagesToUpload = imagesToUpload;
	}

	if (rest.updatedAvatarImg?.key) {
		rest.updatedAvatarImg = undefined;
	}

	if (rest.updatedBackgroundImg?.key) {
		rest.updatedBackgroundImg = undefined;
	}

	const interestIds = (rest.interestsToInsert ?? []).map(interest => interest.interestId);
	const interestsIdsToDelete = (communityProfile.interests ?? [])
		.map(interest => interest.interestId)
		.filter(id => !interestIds.includes(id));

	if (interestsIdsToDelete.length) {
		rest.interestsIdsToDelete = interestsIdsToDelete;
	}

	return { ...rest };
};
